import { useState } from "react";
import { useLocation } from "wouter";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, CheckCircle2, XCircle, ArrowRight, Mail, UserCheck } from "lucide-react";

interface InvitationPreview {
  email: string;
  firstName?: string;
  lastName?: string;
  tenantName?: string;
  role?: string;
  expiresAt?: string;
}

interface AcceptInviteResponse {
  success: boolean;
  message?: string;
  user?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
    role: string;
    username: string;
  };
}

export default function AcceptInvitePage() {
  const [, setLocation] = useLocation();
  // Read the token synchronously so the initial render already has the correct
  // value — avoids a brief null-token flash that shows "Invalid Invitation"
  // before the query can run and return the real error state.
  const [token] = useState<string | null>(() => new URLSearchParams(window.location.search).get('token'));

  const { data: previewData, isLoading: previewLoading, error: previewError } = useQuery<{ success: boolean; invitation: InvitationPreview; message?: string }>({
    queryKey: ['/api/accept-invite', token],
    queryFn: async () => {
      const response = await fetch(`/api/accept-invite?token=${encodeURIComponent(token!)}`, {
        method: 'GET',
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        const error = new Error(data.message || 'Failed to load invitation') as Error & { expired?: boolean };
        error.expired = response.status === 410 || data.expired === true;
        throw error;
      }
      return data;
    },
    enabled: !!token,
    retry: false,
  });

  const acceptInviteMutation = useMutation({
    mutationFn: async (inviteToken: string) => {
      const response = await fetch(`/api/accept-invite?token=${encodeURIComponent(inviteToken)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to accept invitation');
      }
      return data as AcceptInviteResponse;
    },
  });

  const handleAccept = () => {
    if (token) {
      acceptInviteMutation.mutate(token);
    }
  };

  const handleGoToLogin = () => {
    setLocation('/');
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="max-w-md w-full" data-testid="card-error">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" data-testid="icon-error" />
            </div>
            <CardTitle data-testid="text-error-title">Invalid Invitation</CardTitle>
            <CardDescription data-testid="text-error-description">
              No invitation token provided
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={handleGoToLogin} 
              className="w-full" 
              data-testid="button-go-home"
            >
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (previewLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="max-w-md w-full" data-testid="card-loading">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin" data-testid="icon-loading" />
            </div>
            <CardTitle data-testid="text-loading-title">Loading Invitation</CardTitle>
            <CardDescription data-testid="text-loading-description">
              Please wait...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (previewError || !previewData) {
    const errorMessage = previewError instanceof Error
      ? previewError.message
      : 'This invitation link is invalid or has expired.';
    const isExpired = previewError instanceof Error
      && (previewError as Error & { expired?: boolean }).expired === true;

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="max-w-md w-full" data-testid="card-error">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" data-testid="icon-error" />
            </div>
            <CardTitle data-testid="text-error-title">{isExpired ? 'Invitation Expired' : 'Invitation Error'}</CardTitle>
            <CardDescription data-testid="text-error-description">
              {isExpired ? 'This invitation link is no longer valid' : 'Unable to load the invitation'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive" data-testid="alert-error">
              <AlertDescription data-testid="text-error-message">
                {errorMessage}
              </AlertDescription>
            </Alert>
            <Button
              onClick={handleGoToLogin}
              className="w-full"
              data-testid="button-go-home"
            >
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (acceptInviteMutation.isPending) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="max-w-md w-full" data-testid="card-loading">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
              <Loader2 className="w-6 h-6 text-blue-600 dark:text-blue-400 animate-spin" data-testid="icon-loading" />
            </div>
            <CardTitle data-testid="text-loading-title">Processing Invitation</CardTitle>
            <CardDescription data-testid="text-loading-description">
              Please wait while we set up your account...
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (acceptInviteMutation.isError) {
    const errorMessage = acceptInviteMutation.error instanceof Error
      ? acceptInviteMutation.error.message
      : 'An unexpected error occurred';

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="max-w-md w-full" data-testid="card-error">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900 flex items-center justify-center">
              <XCircle className="w-6 h-6 text-red-600 dark:text-red-400" data-testid="icon-error" />
            </div>
            <CardTitle data-testid="text-error-title">Invitation Error</CardTitle>
            <CardDescription data-testid="text-error-description">
              Unable to accept the invitation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive" data-testid="alert-error">
              <AlertDescription data-testid="text-error-message">
                {errorMessage}
              </AlertDescription>
            </Alert>
            <Button
              onClick={handleGoToLogin}
              className="w-full"
              data-testid="button-go-home"
            >
              Go to Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (acceptInviteMutation.isSuccess) {
    const responseData = acceptInviteMutation.data;
    const user = responseData?.user;
    const isNewAccount = !responseData?.message?.includes('existing') && !responseData?.message?.includes('updated');

    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-4">
        <Card className="max-w-md w-full" data-testid="card-success">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center">
              <CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" data-testid="icon-success" />
            </div>
            <CardTitle data-testid="text-success-title">You're In!</CardTitle>
            <CardDescription data-testid="text-success-description">
              {responseData?.message || "You have been added to the organization"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {isNewAccount ? (
              <Alert className="bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800" data-testid="alert-credentials">
                <Mail className="h-4 w-4" />
                <AlertDescription className="space-y-1">
                  <p className="font-semibold text-sm" data-testid="text-credentials-header">
                    Check your email
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400" data-testid="text-credentials-info">
                    Your login credentials have been sent to <strong>{user?.email}</strong>. Use them to sign in and set a permanent password.
                  </p>
                </AlertDescription>
              </Alert>
            ) : (
              <Alert className="bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800" data-testid="alert-existing-user">
                <AlertDescription className="text-sm text-green-800 dark:text-green-200">
                  Your existing account has been linked to this organization. Just sign in as usual and you'll have access.
                </AlertDescription>
              </Alert>
            )}

            {user && (
              <div className="text-center text-sm text-gray-600 dark:text-gray-400" data-testid="text-user-info">
                {user.firstName && <p>Welcome, {user.firstName} {user.lastName}!</p>}
                <p className="text-xs mt-1 capitalize">Role: {user.role}</p>
              </div>
            )}

            <Button
              onClick={handleGoToLogin}
              className="w-full"
              data-testid="button-go-login"
            >
              Go to Login
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const invitation = previewData.invitation;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-4">
      <Card className="max-w-md w-full" data-testid="card-confirm">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
            <UserCheck className="w-6 h-6 text-blue-600 dark:text-blue-400" data-testid="icon-invite" />
          </div>
          <CardTitle data-testid="text-confirm-title">You've been invited</CardTitle>
          <CardDescription data-testid="text-confirm-description">
            {invitation.tenantName
              ? `You have been invited to join ${invitation.tenantName}`
              : 'You have been invited to join an organization'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md bg-gray-50 dark:bg-gray-800 p-3 text-sm space-y-1">
            <p className="text-gray-700 dark:text-gray-300">
              <span className="font-medium">Email:</span> {invitation.email}
            </p>
            {invitation.role && (
              <p className="text-gray-700 dark:text-gray-300 capitalize">
                <span className="font-medium">Role:</span> {invitation.role}
              </p>
            )}
            {(invitation.firstName || invitation.lastName) && (
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-medium">Name:</span> {[invitation.firstName, invitation.lastName].filter(Boolean).join(' ')}
              </p>
            )}
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            By clicking Accept, you agree to join this organization. If you did not expect this invitation, you can safely close this page.
          </p>
          <Button
            onClick={handleAccept}
            className="w-full"
            data-testid="button-accept"
          >
            Accept Invitation
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            onClick={handleGoToLogin}
            className="w-full"
            data-testid="button-decline"
          >
            Decline
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
